/**
 * @fileoverview Hook for fetching navigation data (categories, subcategories, projects)
 * from the API. Handles auth state and mutation refresh.
 */

import { useState, useEffect, useCallback } from 'react';
import { sessionAuth } from '@/api';
import { apiClient, Area, Project, Resource } from '@/api';
import { getErrorMessage } from '@/shared/utils';

export interface NavigationData {
  projects: Project[];
  resources: Resource[];
  areas: Area[];
}

export interface UseNavigationDataReturn {
  data: NavigationData;
  loading: boolean;
  error: string | null;
  dataLoaded: boolean;
  refresh: () => Promise<void>;
}

const EMPTY_DATA: NavigationData = {
  projects: [],
  resources: [],
  areas: [],
};

/**
 * Fetches navigation-related data from the API (categories, subcategories, projects).
 * Waits for session auth to be ready before fetching.
 */
export function useNavigationData(): UseNavigationDataReturn {
  const [data, setData] = useState<NavigationData>(EMPTY_DATA);
  const [loading, setLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadNavigationData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [projectsResponse, resourcesResponse, areasResponse] = await Promise.all([
        apiClient.getMany<Project>('project', { page: '0', pageSize: '100', sortField: 'name', sortOrder: 'asc' }),
        apiClient.getMany<Resource>('resource', { page: '0', pageSize: '100', sortField: 'name', sortOrder: 'asc' }),
        apiClient.getMany<Area>('area', { page: '0', pageSize: '100', sortField: 'name', sortOrder: 'asc' }),
      ]);

      const activeProjects = projectsResponse.items.filter((proj) => !proj.is_deleted);
      const activeResources = resourcesResponse.items.filter((res) => !res.is_deleted);
      const activeAreas = areasResponse.items.filter((area) => !area.is_deleted);

      setData({
        projects: activeProjects,
        resources: activeResources,
        areas: activeAreas,
      });
      setDataLoaded(true);
    } catch (err) {
      console.error('Failed to load navigation data:', err);
      setError(getErrorMessage(err, 'Failed to load navigation data'));
      setData(EMPTY_DATA);
    } finally {
      setLoading(false);
    }
  }, []);

  // Wait for session auth, then load data
  useEffect(() => {
    let isMounted = true;
    let dataLoadStarted = false;

    const startLoadingData = () => {
      if (!dataLoadStarted && isMounted) {
        dataLoadStarted = true;
        loadNavigationData();
      }
    };

    // Check immediately in case auth is already ready
    if (sessionAuth.currentUser) {
      startLoadingData();
    }

    // Listen for auth state changes. After a sign-out (e.g. an expired token),
    // load again on the next sign-in instead of keeping the failed/old data.
    const unsubscribe = sessionAuth.onAuthStateChanged((user) => {
      if (user) {
        startLoadingData();
      } else {
        dataLoadStarted = false;
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [loadNavigationData]);

  // Listen for data mutations to refresh
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { endpoint?: string } | undefined;
      const endpoint = detail?.endpoint;
      if (endpoint === 'project' || endpoint === 'resource' || endpoint === 'area') {
        loadNavigationData();
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('data-source-mutated', handler as EventListener);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('data-source-mutated', handler as EventListener);
      }
    };
  }, [loadNavigationData]);

  return {
    data,
    loading,
    error,
    dataLoaded,
    refresh: loadNavigationData,
  };
}

